import { Link } from "react-router-dom";
import { useState } from "react";
import { CalendarDays, Download, SquarePen, X } from "lucide-react";
// html2canvas-pro, not html2canvas: 1.4.1 (unmaintained since 2022) mis-computes the text
// baseline and draws every string ~8px below where the browser puts it, which showed up as table
// rows whose text sat low instead of centred between the rules. The fork fixes the baseline
// maths; measured on the Student Strength table, the error goes from 8.0px to 0.5px. Same API,
// and `data-html2canvas-ignore` is still honoured.
import html2canvas from "html2canvas-pro";
import jsPDF from "jspdf";

const loadImage = (src) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Failed to load image: ${src}`));
    img.src = src;
  });

// Page geometry, in mm. Only the width is fixed — each page's height is derived from the
// content it holds, so pages end where the dashboard ends instead of padding out to A4.
const PAGE_WIDTH = 210;
const MARGIN = 6;
const SIDEBAR_WIDTH = 32;
const COLUMN_GAP = 4;
const FOOTER_GAP = 2;
const FOOTER_HEIGHT = 1;

// Capture resolution is derived from the printed size rather than fixed, so pages carry the
// pixels 300dpi print actually needs and no more. A flat `scale: 3` rendered a ~1400px-wide
// dashboard at 4200px into a 162mm slot — roughly 660dpi, i.e. most of the file was detail no
// printer or screen can resolve.
const TARGET_DPI = 300;
const MM_PER_INCH = 25.4;
const MIN_SCALE = 1;
const MAX_SCALE = 3;
const JPEG_QUALITY = 0.92;

// The sidebar PNGs are authored full-page-tall: the menu sits in the top third, followed by a
// divider, a "Log Out" row, then a long empty navy tail. Only the menu belongs in the report, so
// the image is cut at that divider — the last row that spans essentially the whole column.
const SIDEBAR_BG_PROBE = 0.8; // sample the empty tail to learn the navy background colour
const FULL_WIDTH_RATIO = 0.85;
const EDGE_MARGIN = 0.02; // ignore 1px border artefacts at the very top/bottom edges
const BG_TOLERANCE = 18;

const cropSidebarToMenu = (img) => {
  const width = img.naturalWidth;
  const height = img.naturalHeight;

  const source = document.createElement("canvas");
  source.width = width;
  source.height = height;
  const sourceCtx = source.getContext("2d", { willReadFrequently: true });
  sourceCtx.drawImage(img, 0, 0);

  const { data } = sourceCtx.getImageData(0, 0, width, height);
  const offsetOf = (x, y) => (y * width + x) * 4;

  const probe = offsetOf(Math.floor(width / 2), Math.floor(height * SIDEBAR_BG_PROBE));
  const bg = [data[probe], data[probe + 1], data[probe + 2]];
  const isBackground = (offset) =>
    Math.abs(data[offset] - bg[0]) +
      Math.abs(data[offset + 1] - bg[1]) +
      Math.abs(data[offset + 2] - bg[2]) <=
    BG_TOLERANCE;

  const searchFrom = Math.floor(height * (1 - EDGE_MARGIN));
  const searchTo = Math.floor(height * EDGE_MARGIN);

  let cut = -1;
  let lastContentRow = -1;
  for (let y = searchFrom; y >= searchTo; y -= 1) {
    let lit = 0;
    for (let x = 0; x < width; x += 1) {
      if (!isBackground(offsetOf(x, y))) lit += 1;
    }
    if (lastContentRow === -1 && lit > 2) lastContentRow = y;
    if (lit > width * FULL_WIDTH_RATIO) {
      cut = y;
      break;
    }
  }

  // No divider found (an unexpected sidebar layout): fall back to trimming the empty tail only.
  const croppedHeight = cut > 0 ? cut : Math.max(lastContentRow, 1);

  const out = document.createElement("canvas");
  out.width = width;
  out.height = croppedHeight;
  out.getContext("2d").drawImage(source, 0, 0, width, croppedHeight, 0, 0, width, croppedHeight);

  // bg is handed back so the page can extend the navy column behind the cropped menu, keeping
  // the sidebar full height instead of leaving it a floating block.
  return { dataUrl: out.toDataURL("image/png"), width, height: croppedHeight, bg };
};

const OverviewHeader = ({
  dashboardRef,
  perumbakkamRef,
  kvrRef,
  thiruvanmiyurRef,
  thirumudivakkamRef,
  hyderabadRef,
  academicsRef,
  transportRef,
  hrRef,
  bestSchoolRef,   // ✅ BestSchool last
  currentMonth = "May 2026",
}) => {
  const [isExporting, setIsExporting] = useState(false);
  const [progressText, setProgressText] = useState("");
  const [exportError, setExportError] = useState("");
  const [showPreview, setShowPreview] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");

  const downloadPDF = async () => {
    setIsExporting(true);
    setProgressText("Initializing...");
    setExportError("");

    try {
      const pdf = new jsPDF("p", "mm", "a4");

      const footerImg = await loadImage(`${process.env.PUBLIC_URL}/ColourPDF.png`);

      // Map by name
      const sidebarMap = {
        dashboard: `${process.env.PUBLIC_URL}/DashboardOverview.png`,
        perumbakkam: `${process.env.PUBLIC_URL}/PerumbakkamCampusPDF.png`,
        kvr: `${process.env.PUBLIC_URL}/KVRCampusPDF.png`,
        thiruvanmiyur: `${process.env.PUBLIC_URL}/ThiruvanmiyurCampusPDF.png`,
        thirumudivakkam: `${process.env.PUBLIC_URL}/ThirumudivakkamPDF.png`,
        hyderabad: `${process.env.PUBLIC_URL}/HyderabadPDF.png`,
        academics: `${process.env.PUBLIC_URL}/AcademicsPDF.png`,
        transport: `${process.env.PUBLIC_URL}/TransportPDF.png`,
        hr: `${process.env.PUBLIC_URL}/HUMANRESOURCESPDF.png`,
        bestSchool: `${process.env.PUBLIC_URL}/BESTSCHOOLPDF.png`,
      };

      setProgressText("Preparing…");
      const sidebars = Object.fromEntries(
        await Promise.all(
          Object.entries(sidebarMap).map(async ([key, src]) => [
            key,
            cropSidebarToMenu(await loadImage(src)),
          ])
        )
      );

      const capturePage = async (ref, name) => {
        if (!ref?.current) return;

        const contentWidth = PAGE_WIDTH - MARGIN * 2 - SIDEBAR_WIDTH - COLUMN_GAP;

        const originalWidth = ref.current.style.width;
        if (name !== "dashboard" && dashboardRef?.current) {
          ref.current.style.width = dashboardRef.current.offsetWidth + "px";
        }

        // Read the laid-out width only after the override above, so the scale matches what is
        // actually about to be rasterised.
        const printWidthPx = (contentWidth / MM_PER_INCH) * TARGET_DPI;
        const elementWidth = ref.current.offsetWidth || printWidthPx;
        const scale = Math.min(MAX_SCALE, Math.max(MIN_SCALE, printWidthPx / elementWidth));

        const canvas = await html2canvas(ref.current, { scale, useCORS: true });

        if (name !== "dashboard" && dashboardRef?.current) {
          ref.current.style.width = originalWidth;
        }

        const imgData = canvas.toDataURL("image/jpeg", JPEG_QUALITY);
        const sidebar = sidebars[name];

        const contentHeight = (canvas.height * contentWidth) / canvas.width;
        const menuHeight = (sidebar.height * SIDEBAR_WIDTH) / sidebar.width;

        // The page is exactly as tall as its taller column, so there's no dead space above or
        // below the report.
        const bodyHeight = Math.max(contentHeight, menuHeight);
        const footerY = MARGIN + bodyHeight + FOOTER_GAP;
        const pageHeight = footerY + FOOTER_HEIGHT + MARGIN;

        // jsPDF normalises a page to its orientation, swapping the two dimensions when they
        // disagree — so a page shorter than it is wide has to be declared landscape, or the
        // 210mm width would be flipped into the height.
        pdf.addPage([PAGE_WIDTH, pageHeight], pageHeight >= PAGE_WIDTH ? "p" : "l");

        // The navy column runs the full height of the page body so it reads as a sidebar rather
        // than a floating block; the logo/menu artwork sits at the top of it, as in the app.
        pdf.setFillColor(sidebar.bg[0], sidebar.bg[1], sidebar.bg[2]);
        pdf.rect(MARGIN, MARGIN, SIDEBAR_WIDTH, bodyHeight, "F");

        // Without an explicit compression level jsPDF embeds PNGs as raw uncompressed bitmaps —
        // ~2MB each here, which dwarfed everything else in the file. These are flat navy artwork,
        // so deflate crushes them losslessly.
        pdf.addImage(
          sidebar.dataUrl,
          "PNG",
          MARGIN,
          MARGIN,
          SIDEBAR_WIDTH,
          menuHeight,
          `sidebar-${name}`,
          "SLOW"
        );

        pdf.addImage(
          imgData,
          "JPEG",
          MARGIN + SIDEBAR_WIDTH + COLUMN_GAP,
          MARGIN + (bodyHeight - contentHeight) / 2,
          contentWidth,
          contentHeight
        );

        // The footer strip is identical on every page — the alias makes jsPDF embed it once.
        pdf.addImage(
          footerImg,
          "PNG",
          MARGIN,
          footerY,
          PAGE_WIDTH - MARGIN * 2,
          FOOTER_HEIGHT,
          "footer",
          "SLOW"
        );
      };

      // Capture all dashboards
      const totalPages = 10;
      let currentPage = 1;
      const updateProgress = () => {
        setProgressText(`Capturing ${currentPage}/${totalPages}...`);
        currentPage++;
      };

      updateProgress(); await capturePage(dashboardRef, "dashboard");
      updateProgress(); await capturePage(perumbakkamRef, "perumbakkam");
      updateProgress(); await capturePage(kvrRef, "kvr");
      updateProgress(); await capturePage(thiruvanmiyurRef, "thiruvanmiyur");
      updateProgress(); await capturePage(thirumudivakkamRef, "thirumudivakkam");
      updateProgress(); await capturePage(hyderabadRef, "hyderabad");
      updateProgress(); await capturePage(academicsRef, "academics");
      updateProgress(); await capturePage(transportRef, "transport");
      updateProgress(); await capturePage(hrRef, "hr");
      updateProgress(); await capturePage(bestSchoolRef, "bestSchool");

      setProgressText("Generating PDF...");

      // Every captured page adds its own content-sized page, leaving jsPDF's initial blank A4
      // in front of them.
      if (pdf.getNumberOfPages() > 1) {
        pdf.deletePage(1);
      }

      const blob = pdf.output("blob");
      const url = URL.createObjectURL(blob);
      setPreviewUrl(url);
      setShowPreview(true);
    } catch (error) {
      console.error("PDF Export Error:", error);
      setExportError(error.message || "Failed to generate PDF. Please try again.");
    } finally {
      setIsExporting(false);
      setProgressText("");
    }
  };

  const handleDownload = () => {
    const a = document.createElement("a");
    a.href = previewUrl;
    a.download = `${currentMonth} Monthly Report.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setShowPreview(false);
  };

  return (
    <div className="flex">
      <div className="relative flex-1 overflow-hidden rounded-r-2xl bg-gradient-to-r from-violet-600 via-purple-600 to-indigo-600 p-6 text-white shadow-lg">
        <div className="relative z-10 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Overview</h1>
            <p className="mt-1 text-sm text-white/80">
              Casagrand International Group of Schools | Academic Year 2025–26
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <button className="flex items-center justify-center gap-2 w-38 rounded-lg border border-white/20 bg-white/10 px-4 py-2 text-sm font-medium backdrop-blur transition hover:bg-white/20">
              <CalendarDays size={18} />
              <span className="text-base">{currentMonth}</span>
            </button>
            <button
              data-html2canvas-ignore="true"
              onClick={downloadPDF}
              disabled={isExporting}
              className="flex items-center justify-center gap-2 w-48 rounded-lg border border-white/20 bg-white/10 px-4 py-2 text-sm font-medium backdrop-blur transition hover:bg-white/20 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <Download size={18} className={isExporting ? "animate-pulse" : ""} />
              <span className="text-base">{isExporting ? (progressText || "Generating…") : "Export"}</span>
            </button>
            <Link data-html2canvas-ignore="true" to={"/edit/mainDashboard"} className="flex items-center justify-center gap-2 w-38 rounded-lg bg-white px-4 py-2 text-sm font-semibold text-purple-700 transition hover:bg-purple-50">
              <SquarePen size={18} />
              <span className="text-base">Edit</span>
            </Link>
          </div>
        </div>
        {exportError && (
          <p className="relative z-10 mt-3 rounded-lg bg-red-500/20 px-3 py-2 text-sm text-red-50">
            {exportError}
          </p>
        )}
      </div>

      {/* PDF Preview Modal */}
      {showPreview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="flex h-[90vh] w-[90vw] max-w-5xl flex-col rounded-2xl bg-white shadow-2xl overflow-hidden">
            <div className="flex items-center justify-between border-b px-6 py-4 bg-gray-50">
              <h2 className="text-xl font-semibold text-gray-800">PDF Preview</h2>
              <button
                onClick={() => setShowPreview(false)}
                className="rounded-full p-2 text-gray-500 hover:bg-gray-200 hover:text-gray-800 transition"
              >
                <X size={24} />
              </button>
            </div>
            
            <div className="flex-1 bg-gray-200 p-4 overflow-hidden">
              <iframe
                src={`${previewUrl}#toolbar=0`}
                className="h-full w-full rounded border border-gray-300 shadow-sm"
                title="PDF Preview"
              />
            </div>
            
            <div className="flex items-center justify-end gap-3 border-t px-6 py-4 bg-gray-50">
              <button
                onClick={() => setShowPreview(false)}
                className="rounded-lg px-5 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-200 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleDownload}
                className="flex items-center gap-2 rounded-lg bg-purple-600 px-6 py-2.5 text-sm font-medium text-white hover:bg-purple-700 shadow-md transition"
              >
                <Download size={18} />
                Download PDF
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OverviewHeader;
